import {
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { Modal } from 'antd';
import useMessage from 'antd/es/message/useMessage';
import React, { useState } from 'react';
import { formatDate } from '../../../common/dateUtils';
import BaseDropdown from '../../../components/UI/Dropdown/BaseDropdown';
import { BaseAttachment } from '../../../types/BaseAttachment';
import '../styles/TaskView.scss';
import { supportedImageTypes, TaskFilePreview } from './TaskFilePreview';

interface BaseAttachmentsProps<T extends BaseAttachment> {
  attachments: T[];
  onDeleteAttachment: (attachment: T) => void;
  renderHeader?: () => React.ReactNode;
  className?: string;
  allowDelete?: boolean;
  isDraftMode?: boolean;
}

export const BaseAttachments = <T extends BaseAttachment>({
  attachments,
  onDeleteAttachment,
  renderHeader,
  className = '',
  allowDelete = true,
  isDraftMode = false,
}: BaseAttachmentsProps<T>) => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<T | null>(null);
  const [messageApi, messageContextHolder] = useMessage();

  const menuItems = [
    {
      key: 'preview',
      label: <div className="text-brand-grey-dark text-sm font-normal">Preview</div>,
      icon: <EyeOutlined className="text-neutral-900" />,
    },
    {
      key: 'download',
      label: <div className="text-brand-grey-dark text-sm font-normal">Download</div>,
      icon: <DownloadOutlined className="text-neutral-900" />,
    },
    ...(allowDelete
      ? [
          {
            type: 'divider' as const,
            key: 'divider',
          },
          {
            key: 'delete',
            label: <div className="justify-start text-red-600 text-sm font-normal">Delete</div>,
            icon: <DeleteOutlined className="text-red-600" />,
          },
        ]
      : []),
  ];

  const handlePreview = (attachment: T) => {
    if (supportedImageTypes.includes(attachment.filename.split('.').pop()?.toLowerCase() || '')) {
      setPreviewFile(attachment);
      setPreviewVisible(true);
    } else {
      window.open(attachment.url, '_blank');
    }
  };

  const handleDownload = async (attachment: T) => {
    try {
      const response = await fetch(attachment.url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = attachment.filename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      void messageApi.success('Download started');
    } catch (error) {
      console.error('Download failed:', error);
      void messageApi.error('Failed to download file');
    }
  };

  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <>
      {messageContextHolder}
      <div className={className}>
        {renderHeader && renderHeader()}
        <div className="flex flex-wrap gap-3">
          {attachments.map((attachment: T) => (
            <div
              key={attachment.id}
              className={`flex justify-between items-center gap-2 ${isDraftMode ? 'px-2' : 'px-3'} py-1.5 border border-solid border-blue-200 bg-blue-100 rounded-md cursor-pointer relative group`}
              onClick={() => handlePreview(attachment)}
            >
              <div className="text-neutral-900 w-9 h-9 flex items-center justify-center">
                {attachment.filename.toLowerCase().endsWith('.jpg') ||
                attachment.filename.toLowerCase().endsWith('.jpeg') ||
                attachment.filename.toLowerCase().endsWith('.png') ||
                attachment.filename.toLowerCase().endsWith('.gif') ? (
                  <img src={attachment.url} alt={attachment.filename} className="w-full h-full object-cover rounded" />
                ) : (
                  <FileOutlined className="text-2xl" />
                )}
              </div>
              <div className="flex flex-col gap-1">
                <div className="max-w-[240px] text-brand-grey-dark text-xs font-normal truncate">
                  {attachment.filename}
                </div>
                <div className="text-neutral-900 text-xs font-normal">
                  {formatDate(new Date(attachment.createdAt), 'DD MMM YYYY, HH:mm')}
                </div>
              </div>
              <div className={isDraftMode ? 'self-start' : ''}>
                {isDraftMode ? (
                  // Draft mode: Show simple X icon for delete (visible on hover)
                  <CloseOutlined
                    className="text-neutral-900 cursor-pointer hover:bg-neutral-200 rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      onDeleteAttachment(attachment);
                    }}
                  />
                ) : (
                  // Normal mode: Show three dots menu on hover
                  <>
                    <div className="w-[14px] h-[14px] text-neutral-900 group-hover:hidden" />
                    <BaseDropdown
                      menu={{
                        items: menuItems,
                        onClick: (e) => {
                          e.domEvent.stopPropagation();
                          if (e.key === 'preview') {
                            handlePreview(attachment);
                          } else if (e.key === 'download') {
                            void handleDownload(attachment);
                          } else if (e.key === 'delete') {
                            onDeleteAttachment(attachment);
                          }
                        },
                      }}
                      trigger={['click']}
                      placement="bottom"
                      overlayStyle={{ width: 240 }}
                    >
                      <MoreOutlined
                        className="text-neutral-900 hidden group-hover:block cursor-pointer"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      />
                    </BaseDropdown>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Modal
        title={previewFile?.filename}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        style={{ top: 0, padding: 0 }}
        footer={null}
        width="90%"
        destroyOnClose
        centered
      >
        <div className="h-[calc(100vh-120px)] flex items-center justify-center">
          {previewFile && <TaskFilePreview attachment={previewFile} />}
        </div>
      </Modal>
    </>
  );
};
